// src/pages/products/hooks/useProductsPage.ts
import React from "react";

import {
  useProductsList,
  useProductDetail,
  useCreateProductMutation,
  useUpdateProductMutation,
  useDeleteProductMutation,
} from "@/api/features/products";
import type {
  Product,
  ProductWithConfig,
  CreateProductPayload,
  UpdateProductPayload,
} from "@/api/features/products/types";

const PAGE_SIZE = 10;

/* ======================================================================
 * Public hook result type
 * ====================================================================== */

export type ProductsFilters = {
  name?: string;
  slug?: string;
};

export interface UseProductsPageResult {
  // Filters / pagination
  search: string;
  setSearch: (v: string) => void;

  filters: ProductsFilters;
  setFilters: React.Dispatch<React.SetStateAction<ProductsFilters>>;

  page: number;
  setPage: (p: number) => void;
  pageSize: number;

  // List data
  rows: Product[];
  total: number;
  isLoading: boolean;
  isFetching: boolean;

  // Create modal
  createOpen: boolean;
  setCreateOpen: (open: boolean) => void;

  // Edit modal
  editingId: number | null;
  setEditingId: (id: number | null) => void;

  // Delete confirm
  confirmDelete: {
    open: boolean;
    row: Product | null;
  };
  setConfirmDelete: (v: { open: boolean; row: Product | null }) => void;

  // Detail (for edit)
  editingValue: ProductWithConfig | null;
  isDetailLoading: boolean;

  // Mutation flags / errors
  creating: boolean;
  updating: boolean;
  deleting: boolean;
  createError: string | null;
  updateError: string | null;

  // Handlers
  handleOpenEdit: (row: Product) => void;
  handleCloseEdit: () => void;

  handleAskDelete: (row: Product) => void;
  handleConfirmDelete: () => void;

  handleCreate: (payload: CreateProductPayload) => void;
  handleUpdate: (payload: UpdateProductPayload) => void;
}

/* ======================================================================
 * Hook implementation
 * ====================================================================== */

export function useProductsPage(): UseProductsPageResult {
  // -----------------------------
  // Local UI state
  // -----------------------------
  const [search, setSearch] = React.useState("");
  const [filters, setFilters] = React.useState<ProductsFilters>({});
  const [page, setPage] = React.useState(1);

  // Reset to first page whenever a column filter changes.
  React.useEffect(() => {
    setPage(1);
  }, [filters.name, filters.slug]);

  const [createOpen, setCreateOpen] = React.useState(false);
  const [editingId, setEditingId] = React.useState<number | null>(null);

  const [confirmDelete, setConfirmDelete] = React.useState<{
    open: boolean;
    row: Product | null;
  }>({
    open: false,
    row: null,
  });

  // -----------------------------
  // List query
  // -----------------------------
  const {
    data: listRes,
    isLoading,
    isFetching,
  } = useProductsList({
    page,
    limit: PAGE_SIZE,
    q: search.trim() || undefined,
    name: filters.name?.trim() || undefined,
    slug: filters.slug?.trim() || undefined,
  });

  const rows = React.useMemo(() => listRes?.items ?? [], [listRes]);
  const total = listRes?.total ?? 0;

  // -----------------------------
  // Mutations
  // -----------------------------
  const createMutation = useCreateProductMutation();
  const updateMutation = useUpdateProductMutation(editingId ?? 0);
  const deleteMutation = useDeleteProductMutation();

  const creating = createMutation.isPending;
  const updating = updateMutation.isPending;
  const deleting = deleteMutation.isPending;

  const createError =
    (createMutation.error as any)?.message ?? null;
  const updateError =
    (updateMutation.error as any)?.message ?? null;

  // -----------------------------
  // Detail query (for edit)
  // -----------------------------
  const {
    data: detailData,
    isLoading: isDetailLoading,
  } = useProductDetail(editingId);

  const editingValue: ProductWithConfig | null =
    detailData ?? null;

  // -----------------------------
  // Handlers
  // -----------------------------
  const handleOpenEdit = React.useCallback((row: Product) => {
    setEditingId(row.id);
  }, []);

  const handleCloseEdit = React.useCallback(() => {
    setEditingId(null);
  }, []);

  const handleAskDelete = React.useCallback((row: Product) => {
    setConfirmDelete({ open: true, row });
  }, []);

  const handleConfirmDelete = React.useCallback(() => {
    setConfirmDelete((state) => {
      if (!state.row) return state;

      deleteMutation.mutate(state.row.id, {
        onSettled: () =>
          setConfirmDelete({ open: false, row: null }),
      });

      return state;
    });
  }, [deleteMutation]);

  const handleCreate = React.useCallback(
    (payload: CreateProductPayload) => {
      createMutation.mutate(payload, {
        onSuccess: () => {
          setCreateOpen(false);
        },
      });
    },
    [createMutation]
  );

  const handleUpdate = React.useCallback(
    (payload: UpdateProductPayload) => {
      if (!editingId) return;

      updateMutation.mutate(payload, {
        onSuccess: () => {
          setEditingId(null);
        },
      });
    },
    [editingId, updateMutation]
  );

  // -----------------------------
  // Exposed API
  // -----------------------------
  return {
    // filters / pagination
    search,
    setSearch,
    filters,
    setFilters,
    page,
    setPage,
    pageSize: PAGE_SIZE,

    // list data
    rows,
    total,
    isLoading,
    isFetching,

    // modals
    createOpen,
    setCreateOpen,
    editingId,
    setEditingId,

    // delete confirm
    confirmDelete,
    setConfirmDelete,

    // edit detail
    editingValue,
    isDetailLoading,

    // mutation flags / errors
    creating,
    updating,
    deleting,
    createError,
    updateError,

    // handlers
    handleOpenEdit,
    handleCloseEdit,
    handleAskDelete,
    handleConfirmDelete,
    handleCreate,
    handleUpdate,
  };
}
